import React, { useEffect, useState, useMemo } from 'react';
import { useNavPillsIndicator } from 'hooks/useNavPillsIndicator';
import { useSelector } from 'react-redux';
import {
  ChartOptions,
  Chart as ChartJS,
  ArcElement,
} from 'chart.js';
import { Chart, Doughnut, Line } from 'react-chartjs-2';

import DateRangeFilter from 'component/DateRangeFilterDropdown';
import ProjectFilterDropdown from 'component/ProjectFilterDropdown';
import MonitorService from 'service/MonitorService';
import { DashboardConfigProjectResp } from 'domain/payload/response/dashboard/DashboardConfigProjectResp';
import { QCMonitorData } from './qcTypes';
import MultiSelectDropdown from 'component/MultiSelectDropdown';
import CustomDropdown, { DropdownOption } from 'component/CustomDropdown';
import { PageSizeOption } from 'domain/constant/PageSizeOption';

ChartJS.register(ArcElement);

const LINE_COLORS = [
  '#3b82f6', // blue
  '#10b981', // green
  '#f59e0b', // orange
  '#8b5cf6', // purple
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#ef4444', // red
  '#14b8a6', // teal
  '#6366f1', // indigo
  '#f43f5e', // rose
];

const verticalHoverPlugin = {
  id: 'verticalHoverBg',
  afterDraw: (chart: any) => {
    if (chart.tooltip?._active?.length) {
      const activePoint = chart.tooltip._active[0];
      const ctx = chart.ctx;
      const chartArea = chart.chartArea;
      const x = activePoint.element.x;

      ctx.save();

      // 1. Draw a soft column highlight background
      ctx.fillStyle = 'rgba(59, 130, 246, 0.04)'; // very soft primary blue highlight
      const meta = chart.getDatasetMeta(0);
      if (meta && meta.data) {
        const pointsCount = meta.data.length;
        let width = 30;
        if (pointsCount > 1) {
          width = (chartArea.right - chartArea.left) / (pointsCount - 1);
        }
        ctx.fillRect(x - width / 2, chartArea.top, width, chartArea.bottom - chartArea.top);
      }

      // 2. Draw a clean dashed vertical line in the middle
      ctx.beginPath();
      ctx.setLineDash([4, 4]);
      ctx.moveTo(x, chartArea.top);
      ctx.lineTo(x, chartArea.bottom);
      ctx.lineWidth = 1;
      ctx.strokeStyle = 'rgba(59, 130, 246, 0.2)'; // semi-transparent primary blue
      ctx.stroke();

      ctx.restore();
    }
  }
};

const parseDurationToMinutes = (durStr: string): number => {
  if (!durStr || durStr === '-') return 0;
  const minMatch = durStr.match(/(\d+)m/);
  const secMatch = durStr.match(/(\d+)s/);
  let mins = 0;
  let secs = 0;
  if (minMatch) mins = parseInt(minMatch[1], 10);
  if (secMatch) secs = parseInt(secMatch[1], 10);
  return mins + (secs / 60);
};

function LoadingOverlay() {
  return (
    <div
      className="top-0 position-absolute start-0 w-100 h-100 d-flex justify-content-center align-items-center"
      style={{ background: 'rgba(255, 255, 255, 0.6)', zIndex: 10 }}
    >
      <div
        className="spinner-border text-primary"
        role="status"
        style={{ width: 60, height: 60 }}
      >
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  );
}

interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

const AUTO_REFRESH_OPTIONS: DropdownOption[] = [
  { id: 0, name: 'Off' },
  { id: 10000, name: '10 seconds' },
  { id: 300000, name: '5 minutes' },
  { id: 600000, name: '10 minutes' },
];

const QCMonitor: React.FC = () => {
  const { profile } = useSelector((state: any) => state.auth);
  const token = profile?.data?.token;

  const [projectList, setProjectList] = useState<DashboardConfigProjectResp[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>(""); // default to empty, will be set on config fetch
  const [timeRange, setTimeRange] = useState<{ startDate: string | null; endDate: string | null }>({
    startDate: null,
    endDate: null,
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [qcData, setQCData] = useState<QCMonitorData | null>(null);
  const [lastRefresh, setLastRefresh] = useState<string>("");
  const [autoRefresh, setAutoRefresh] = useState<number>(0);

  // Tab State for Breakdowns (QC User, Project, Group Job, Field Officer)
  const [activeTab, setActiveTab] = useState<'user' | 'project' | 'groupJob' | 'officer'>('user');
  const { navRef: activeTabNavRef, indicatorStyle: activeTabIndicatorStyle } = useNavPillsIndicator(activeTab);

  // Search, Sort, Pagination States
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Sub-breakdown states
  const [subBreakdownType, setSubBreakdownType] = useState<'project' | 'groupJob' | 'officer' | 'user'>('project');
  const [expandedRowIds, setExpandedRowIds] = useState<Record<string, boolean>>({});
  const [showMatrixAvg, setShowMatrixAvg] = useState<boolean>(false);
  const [matrixViewMode, setMatrixViewMode] = useState<'table' | 'chart'>('table');
  const [matrixValueMode, setMatrixValueMode] = useState<'daily' | 'cumulative'>('daily');
  const { navRef: matrixValueNavRef, indicatorStyle: matrixValueIndicatorStyle } = useNavPillsIndicator(matrixValueMode);
  const { navRef: matrixViewNavRef, indicatorStyle: matrixViewIndicatorStyle } = useNavPillsIndicator(matrixViewMode);
  const [matrixSortConfig, setMatrixSortConfig] = useState<SortConfig | null>(null);
  const [selectedMatrixReviewers, setSelectedMatrixReviewers] = useState<string[]>([]);
  const [pointStyleCache, setPointStyleCache] = useState<Record<string, HTMLCanvasElement>>({});

  const toggleRowExpand = (rowId: string) => {
    setExpandedRowIds((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const monitorService = MonitorService();

  useEffect(() => {
    fetchConfig();
  }, []);

  useEffect(() => {
    fetchData();
  }, [timeRange, selectedProject]);

  useEffect(() => {
    if (!qcData?.dailyMatrix) return;

    const loadPointStyles = async () => {
      const cache: Record<string, HTMLCanvasElement> = {};
      const promises = qcData.dailyMatrix.map(async (row, idx) => {
        const canvas = document.createElement('canvas');
        canvas.width = 24;
        canvas.height = 24;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Base circle clipping mask
        ctx.save();
        ctx.beginPath();
        ctx.arc(12, 12, 11, 0, Math.PI * 2);
        ctx.clip();

        const color = LINE_COLORS[idx % LINE_COLORS.length];

        if (row.profilePicture) {
          try {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.src = row.profilePicture;
            await new Promise((resolve, reject) => {
              img.onload = resolve;
              img.onerror = reject;
            });
            ctx.drawImage(img, 0, 0, 24, 24);
          } catch (e) {
            ctx.fillStyle = color;
            ctx.fillRect(0, 0, 24, 24);
            ctx.fillStyle = '#ffffff';
            ctx.font = 'bold 8px sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            const initials = row.userName.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
            ctx.fillText(initials, 12, 12);
          }
        } else {
          ctx.fillStyle = color;
          ctx.fillRect(0, 0, 24, 24);
          ctx.fillStyle = '#ffffff';
          ctx.font = 'bold 8px sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          const initials = row.userName.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
          ctx.fillText(initials, 12, 12);
        }
        ctx.restore();

        // Border ring around the avatar/initial point matching user's line color
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(12, 12, 11, 0, Math.PI * 2);
        ctx.stroke();

        cache[row.userId] = canvas;
      });

      await Promise.all(promises);
      setPointStyleCache(cache);
    };

    loadPointStyles();
  }, [qcData]);

  useEffect(() => {
    if (qcData?.dailyMatrix && selectedMatrixReviewers.length > 0) {
      const newIds = qcData.dailyMatrix.map(r => r.userId);
      setSelectedMatrixReviewers(prev => prev.filter(id => newIds.includes(id)));
    }
  }, [qcData]);


  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchData, autoRefresh);
    return () => clearInterval(interval);
  }, [autoRefresh, timeRange, selectedProject]);

  // Set default sub-breakdown when changing active tabs
  useEffect(() => {
    if (activeTab === 'user') setSubBreakdownType('project');
    else if (activeTab === 'project') setSubBreakdownType('user');
    else if (activeTab === 'groupJob') setSubBreakdownType('user');
    else if (activeTab === 'officer') setSubBreakdownType('project');
  }, [activeTab]);

  const fetchConfig = async () => {
    if (!token) return;
    try {
      const res = await monitorService.qcMonitorConfig(token);
      const projects = res.data?.projectList ?? [];
      setProjectList(projects);
      if (projects.length > 0) {
        setSelectedProject(projects[0].id);
      }
    } catch (err) {
      console.error('Error fetching project configurations', err);
    }
  };

  const fetchData = async () => {
    if (!token || !selectedProject) return;
    setLoading(true);
    try {
      const res = await monitorService.qcMonitorDashboard(
        token,
        selectedProject === "all" ? null : selectedProject,
        timeRange.startDate,
        timeRange.endDate
      );
      if (res.data) {
        setQCData(res.data);
      }

      const now = new Date();
      const formatted = now.toLocaleString("id-ID", {
        timeZone: "Asia/Jakarta",
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
      setLastRefresh(formatted.replace(",", ""));
    } catch (err) {
      console.error('Error fetching QC Monitor dashboard data', err);
    } finally {
      setLoading(false);
    }
  };

  // Reset pagination and search when switching tabs or changing filters
  useEffect(() => {
    setCurrentPage(1);
    setSearchTerm("");
    setSortConfig(null);
    setExpandedRowIds({});
  }, [activeTab, selectedProject, timeRange]);

  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key) {
      direction = sortConfig.direction === 'asc' ? 'desc' : 'asc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1);
  };

  const renderSortIndicator = (key: string) => {
    if (sortConfig?.key === key) {
      return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
    }
    return ' ↕';
  };

  const handleMatrixSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (matrixSortConfig && matrixSortConfig.key === key) {
      direction = matrixSortConfig.direction === 'asc' ? 'desc' : 'asc';
    }
    setMatrixSortConfig({ key, direction });
  };

  const renderMatrixSortIndicator = (key: string) => {
    if (matrixSortConfig?.key === key) {
      return matrixSortConfig.direction === 'asc' ? ' ↑' : ' ↓';
    }
    return ' ↕';
  };

  // Get filtered & sorted list based on active tab
  const getProcessedData = () => {
    if (!qcData) return [];

    let rawList: any[] = [];
    if (activeTab === 'user') {
      rawList = qcData.userBreakdown;
    } else if (activeTab === 'project') {
      rawList = qcData.projectBreakdown;
    } else if (activeTab === 'groupJob') {
      rawList = qcData.groupJobBreakdown;
    } else if (activeTab === 'officer') {
      rawList = qcData.officerBreakdown;
    }

    // Filter by search term
    const query = searchTerm.toLowerCase().trim();
    let filtered = rawList;
    if (query) {
      filtered = rawList.filter((row) => {
        if (activeTab === 'project' || activeTab === 'user' || activeTab === 'officer') {
          return row.name.toLowerCase().includes(query);
        } else {
          return (
            row.name.toLowerCase().includes(query) ||
            row.projectName.toLowerCase().includes(query)
          );
        }
      });
    }

    // Sort
    if (sortConfig) {
      const { key, direction } = sortConfig;
      filtered = [...filtered].sort((a, b) => {
        let valA = a[key];
        let valB = b[key];

        if (key === 'avgDuration') {
          const matchA = String(valA).match(/(\d+)m\s+(\d+)s/);
          const matchB = String(valB).match(/(\d+)m\s+(\d+)s/);
          const timeA = matchA ? parseInt(matchA[1]) * 60 + parseInt(matchA[2]) : 0;
          const timeB = matchB ? parseInt(matchB[1]) * 60 + parseInt(matchB[2]) : 0;
          return direction === 'asc' ? timeA - timeB : timeB - timeA;
        }

        if (typeof valA === 'string') {
          return direction === 'asc'
            ? valA.localeCompare(valB)
            : valB.localeCompare(valA);
        } else {
          return direction === 'asc' ? (valA ?? 0) - (valB ?? 0) : (valB ?? 0) - (valA ?? 0);
        }
      });
    }

    return filtered;
  };

  const processedList = getProcessedData();
  const totalPages = Math.ceil(processedList.length / pageSize) || 1;
  const paginatedList = processedList.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const selectedProjectName = selectedProject === 'all'
    ? 'All Projects'
    : projectList.find(p => p.id === selectedProject)?.name || selectedProject;

  // Doughnut Chart Setup
  const doughnutData = {
    labels: qcData?.statusBreakdown.map((s) => s.label) ?? [],
    datasets: [
      {
        data: qcData?.statusBreakdown.map((s) => s.value) ?? [],
        backgroundColor: qcData?.statusBreakdown.map((s) => s.color) ?? [],
        borderWidth: 0,
        cutout: '75%',
      },
    ],
  };

  const doughnutOptions = {
    plugins: {
      legend: { display: false },
      tooltip: { enabled: true },
    },
    maintainAspectRatio: false,
    responsive: true,
  };

  // Trend Chart Setup (Stacked Bar Chart for minimal clean style)
  const trendData = {
    labels: qcData?.trend.map((t) => t.label) ?? [],
    datasets: [
      {
        type: 'bar' as const,
        label: 'Approved',
        backgroundColor: '#22c55e',
        data: qcData?.trend.map((t) => t.approved) ?? [],
        stack: 'Stack 0',
        borderRadius: 4,
      },
      {
        type: 'bar' as const,
        label: 'Rejected',
        backgroundColor: '#ef4444',
        data: qcData?.trend.map((t) => t.rejected) ?? [],
        stack: 'Stack 0',
      },
      {
        type: 'bar' as const,
        label: 'Done (Pending Review)',
        backgroundColor: '#eab308',
        data: qcData?.trend.map((t) => t.done) ?? [],
        stack: 'Stack 0',
      },
      {
        type: 'bar' as const,
        label: 'Archived',
        backgroundColor: '#6b7280',
        data: qcData?.trend.map((t) => t.archived) ?? [],
        stack: 'Stack 0',
        borderRadius: { topLeft: 4, topRight: 4, bottomLeft: 0, bottomRight: 0 },
      },
    ],
  };

  const trendOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          font: { size: 11 },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        ticks: { font: { size: 10 } },
      },
      y: {
        stacked: true,
        border: { dash: [4, 4] },
        grid: { color: '#e2e8f0' },
        ticks: { font: { size: 10 } },
      },
    },
  };

  // Daily Matrix Chart Configs
  const matrixDates = qcData?.dailyMatrix?.[0]?.days.map((d) => d.date) ?? [];
  const filteredMatrixRows = qcData?.dailyMatrix?.filter((row) =>
    selectedMatrixReviewers.length === 0 || selectedMatrixReviewers.includes(row.userId)
  ) ?? [];

  const dropdownOptions = qcData?.dailyMatrix?.map((row) => ({
    code: row.userId,
    name: row.userName,
  })) ?? [];

  const sortedMatrixRows = useMemo(() => {
    return [...filteredMatrixRows].sort((a: any, b: any) => {
      if (!matrixSortConfig) return 0;
      const { key, direction } = matrixSortConfig;
      const multiplier = direction === 'asc' ? 1 : -1;

      if (key === 'name') {
        return a.userName.localeCompare(b.userName) * multiplier;
      }

      if (key === 'total') {
        const totalA = a.days.reduce((sum: number, d: any) => sum + d.count, 0);
        const totalB = b.days.reduce((sum: number, d: any) => sum + d.count, 0);
        return (totalA - totalB) * multiplier;
      }

      const dayA = a.days.find((d: any) => d.date === key);
      const dayB = b.days.find((d: any) => d.date === key);

      const valA = showMatrixAvg ? parseDurationToMinutes(dayA?.avgDuration || '') : (dayA?.count ?? 0);
      const valB = showMatrixAvg ? parseDurationToMinutes(dayB?.avgDuration || '') : (dayB?.count ?? 0);

      return (valA - valB) * multiplier;
    });
  }, [filteredMatrixRows, matrixSortConfig, showMatrixAvg]);

  const matrixChartData = {
    labels: matrixDates,
    datasets: filteredMatrixRows.map((row, idx) => {
      const color = LINE_COLORS[idx % LINE_COLORS.length];
      const daysCount = row.days.length;
      const avatarCanvas = pointStyleCache[row.userId];

      let runningSum = 0;
      const dataPoints = row.days.map((d) => {
        if (matrixValueMode === 'cumulative') {
          if (showMatrixAvg) {
            runningSum += parseDurationToMinutes(d.avgDuration);
            return runningSum;
          } else {
            runningSum += d.count;
            return runningSum;
          }
        }
        return showMatrixAvg ? parseDurationToMinutes(d.avgDuration) : d.count;
      });

      return {
        label: row.userName,
        data: dataPoints,
        borderColor: color,
        backgroundColor: color + '1a',
        tension: 0.3,
        borderWidth: 2,
        pointStyle: (context: any) => {
          if (context.index === undefined || context.index === daysCount - 1) {
            return avatarCanvas || 'circle';
          }
          return 'circle';
        },
        pointRadius: (context: any) => {
          if (context.index === undefined || context.index === daysCount - 1) {
            return avatarCanvas ? 12 : 3;
          }
          return 3;
        },
        pointHoverStyle: avatarCanvas || 'circle',
        pointHoverRadius: avatarCanvas ? 14 : 5,
        fill: false,
      };
    }) ?? [],
  };

  const matrixChartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          font: { size: 10, weight: 'bold' },
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        itemSort: (a: any, b: any) => b.raw - a.raw,
        callbacks: {
          label: (context: any) => {
            const label = context.dataset.label || '';
            const val = context.raw;
            if (showMatrixAvg) {
              const mins = Math.floor(val);
              const secs = Math.round((val - mins) * 60);
              return `${label}: ${mins}m ${secs}s`;
            }
            return `${label}: ${val} items`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 9 } },
      },
      y: {
        border: { dash: [4, 4] },
        grid: { color: '#f1f5f9' },
        ticks: {
          font: { size: 9 },
          callback: (value: any) => {
            if (showMatrixAvg) {
              return `${value}m`;
            }
            return value;
          },
        },
        title: {
          display: true,
          text: showMatrixAvg ? 'Avg QC Duration (Minutes)' : 'QC Audited Count',
          font: { size: 10, weight: 'bold' },
        },
      },
    },
  };

  return (
    <div className="page-wrapper position-relative" style={{ backgroundColor: '#f8fafc', minHeight: '100vh', padding: '24px 16px' }}>
      <style>{`
        .table-hover tbody tr:hover td {
          background-color: #f8fafc !important;
        }
      `}</style>
      {loading && <LoadingOverlay />}

      <div className="mb-4 d-flex justify-content-between align-items-center">
        <div>
          <h4 className="mb-1 fw-bold text-dark" style={{ letterSpacing: '-0.5px' }}>QC Monitor Dashboard</h4>
        </div>
      </div>

      <div className="card border-0 shadow-sm mb-4 p-3" style={{ borderRadius: '16px', background: '#ffffff' }}>
        <div className="row g-3 align-items-center justify-content-between">
          <div className="col-12 col-lg-7 d-flex flex-wrap align-items-center gap-3">
            <div style={{ width: '240px' }}>
              <ProjectFilterDropdown
                projects={projectList}
                defaultSelectedId={selectedProject || null}
                allowAllProject={false}
                onChange={(p) => setSelectedProject(p ? p.id : "")}
              />
            </div>
            <div>
              <DateRangeFilter
                onChange={(v) => {
                  setTimeRange({ startDate: v.startDate, endDate: v.endDate });
                }}
              />
            </div>
          </div>

          <div className="col-12 col-lg-5 d-flex align-items-center justify-content-lg-end gap-3">
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted fw-semibold" style={{ fontSize: '0.8rem' }}>Auto Refresh</span>
              <CustomDropdown
                value={autoRefresh}
                options={AUTO_REFRESH_OPTIONS}
                onChange={(opt) => setAutoRefresh(Number(opt.id))}
                buttonStyle={{ width: "130px" }}
              />
            </div>
            <button
              type="button"
              className="btn btn-light border-0 shadow-sm"
              onClick={fetchData}
              style={{ width: '42px', height: '38px', borderRadius: '8px', background: '#f1f5f9', color: '#475569' }}
              title="Manual Refresh"
            >
              <i className="fa fa-rotate"></i>
            </button>
            <span className="text-muted text-nowrap d-none d-sm-inline" style={{ fontSize: '0.75rem' }}>
              Last Refreshed: <span className="fw-semibold text-dark">{lastRefresh || "-"}</span>
            </span>
          </div>
        </div>
      </div>

      {qcData && (
        <>
          <div className="row g-3 mb-4">
            <div className="col-12 col-md-4">
              <div
                className="p-3 bg-white shadow-sm border-0 d-flex align-items-center"
                style={{ borderRadius: '16px', borderLeft: '5px solid #eab308', height: '90px' }}
              >
                <div className="rounded-3 p-2 me-3" style={{ background: '#fef9c3', color: '#ca8a04' }}>
                  <i className="fa-solid fa-clock fs-4"></i>
                </div>
                <div>
                  <span className="text-muted text-uppercase fw-semibold d-block" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                    Avg QC Duration (Per User)
                  </span>
                  <h4 className="fw-bold mb-0 text-dark mt-1" style={{ letterSpacing: '-0.5px' }}>
                    {qcData.metrics.avgDuration}
                  </h4>
                </div>
              </div>
            </div>

            <div className="col-12 col-md-4">
              <div
                className="p-3 bg-white shadow-sm border-0 d-flex align-items-center"
                style={{ borderRadius: '16px', borderLeft: '5px solid #3b82f6', height: '90px' }}
              >
                <div className="rounded-3 p-2 me-3" style={{ background: '#dbeafe', color: '#2563eb' }}>
                  <i className="fa-solid fa-database fs-4"></i>
                </div>
                <div>
                  <span className="text-muted text-uppercase fw-semibold d-block" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                    Total QC Volume
                  </span>
                  <h4 className="fw-bold mb-0 text-dark mt-1" style={{ letterSpacing: '-0.5px' }}>
                    {qcData.metrics.totalQC.toLocaleString('id-ID')}
                  </h4>
                </div>
              </div>
            </div>

            <div className="col-12 col-md-4">
              <div
                className="p-3 bg-white shadow-sm border-0 d-flex align-items-center animate__animated animate__fadeIn"
                style={{ borderRadius: '16px', borderLeft: '5px solid #ef4444', height: '90px' }}
              >
                {qcData.metrics.topRejectedOfficer?.profilePicture ? (
                  <img
                    src={qcData.metrics.topRejectedOfficer.profilePicture}
                    alt={qcData.metrics.topRejectedOfficer.name}
                    className="rounded-3 me-3 flex-shrink-0"
                    style={{ width: '42px', height: '42px', objectFit: 'cover' }}
                  />
                ) : (
                  <div
                    className="rounded-3 p-2 me-3 flex-shrink-0 d-flex align-items-center justify-content-center"
                    style={{ background: '#fee2e2', color: '#dc2626', width: '42px', height: '42px' }}
                  >
                    <i className="fa-solid fa-person-digging fs-4"></i>
                  </div>
                )}
                <div className="text-truncate flex-grow-1">
                  <span className="text-muted text-uppercase fw-semibold d-block" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                    Top Rejected Field Officer
                  </span>
                  <div className="d-flex align-items-center gap-2 mt-1">
                    <h5 className="fw-bold mb-0 text-dark text-truncate" style={{ maxWidth: '140px', letterSpacing: '-0.5px', fontSize: '0.9rem' }}>
                      {qcData.metrics.topRejectedOfficer?.name || "N/A"}
                    </h5>
                    {qcData.metrics.topRejectedOfficer?.tribeName && (
                      <span className="badge bg-light text-muted border text-truncate" style={{ fontSize: '8px', fontWeight: 'normal' }}>
                        {qcData.metrics.topRejectedOfficer.tribeName}
                      </span>
                    )}
                  </div>
                  <small className="text-danger fw-semibold" style={{ fontSize: '0.75rem' }}>
                    {qcData.metrics.topRejectedOfficer?.rejectCount ?? 0} Rejections
                  </small>
                </div>
              </div>
            </div>
          </div>

          <div className="row g-4 mb-4">
            <div className="col-12 col-lg-4">
              <div className="card border-0 shadow-sm p-3 h-100" style={{ borderRadius: '16px', background: '#ffffff', minHeight: '340px' }}>
                <span className="text-muted text-uppercase fw-semibold mb-3 d-block" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                  QC Status Breakdown
                </span>

                <div className="d-flex flex-column align-items-center justify-content-center flex-grow-1">
                  <div style={{ width: '160px', height: '160px', position: 'relative', marginBottom: '20px' }}>
                    <Doughnut data={doughnutData} options={doughnutOptions} />
                    <div
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        textAlign: 'center',
                      }}
                    >
                      <span className="text-muted" style={{ fontSize: '0.65rem', display: 'block' }}>Total QC</span>
                      <strong className="fs-5 text-dark" style={{ lineHeight: 1 }}>{qcData.metrics.totalQC}</strong>
                    </div>
                  </div>

                  <div className="d-flex flex-column gap-2 w-100 px-3" style={{ fontSize: '0.8rem' }}>
                    {qcData.statusBreakdown.map((item) => {
                      const percent = qcData.metrics.totalQC > 0
                        ? Math.round((item.value / qcData.metrics.totalQC) * 100)
                        : 0;
                      return (
                        <div className="d-flex align-items-center justify-content-between" key={item.label}>
                          <div className="d-flex align-items-center gap-2">
                            <span className="rounded-circle" style={{ width: '8px', height: '8px', backgroundColor: item.color }} />
                            <span className="text-muted fw-semibold">{item.label}</span>
                          </div>
                          <div>
                            <span className="text-dark fw-bold me-2">{item.value}</span>
                            <span className="text-muted" style={{ fontSize: '0.7rem' }}>({percent}%)</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-lg-8">
              <div className="card border-0 shadow-sm p-3 h-100" style={{ borderRadius: '16px', background: '#ffffff', minHeight: '340px' }}>
                <span className="text-muted text-uppercase fw-semibold mb-3 d-block" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                  QC Trend Developments (QC Users)
                </span>
                <div className="flex-grow-1" style={{ position: 'relative', height: '240px' }}>
                  <Chart type="bar" data={trendData} options={trendOptions} />
                </div>
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm p-4" style={{ borderRadius: '16px', background: '#ffffff' }}>
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 border-bottom pb-3 mb-3">
              <div ref={activeTabNavRef} className="nav-pills-animated" style={{ border: 'none' }}>
                <div className="nav-pill-indicator" style={activeTabIndicatorStyle} />
                <button
                  type="button"
                  className={`nav-link fw-semibold ${activeTab === 'user' ? 'active' : ''}`}
                  onClick={() => setActiveTab('user')}
                >
                  By User QC
                </button>
                <button
                  type="button"
                  className={`nav-link fw-semibold ${activeTab === 'project' ? 'active' : ''}`}
                  onClick={() => setActiveTab('project')}
                >
                  By Project
                </button>
                <button
                  type="button"
                  className={`nav-link fw-semibold ${activeTab === 'groupJob' ? 'active' : ''}`}
                  onClick={() => setActiveTab('groupJob')}
                >
                  By Group Job
                </button>
                <button
                  type="button"
                  className={`nav-link fw-semibold ${activeTab === 'officer' ? 'active' : ''}`}
                  onClick={() => setActiveTab('officer')}
                >
                  By Officer
                </button>
              </div>

              <div className="d-flex flex-wrap align-items-center gap-3">
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted fw-semibold text-nowrap" style={{ fontSize: '0.8rem' }}>Breakdown</span>
                  <CustomDropdown
                    value={subBreakdownType}
                    options={
                      activeTab === 'user' ? [
                        { id: 'project', name: 'By Project' },
                        { id: 'groupJob', name: 'By Group Job' },
                        { id: 'officer', name: 'By Officer' },
                      ] : activeTab === 'project' ? [
                        { id: 'user', name: 'By User QC' },
                        { id: 'groupJob', name: 'By Group Job' },
                      ] : activeTab === 'groupJob' ? [
                        { id: 'user', name: 'By User QC' },
                        { id: 'officer', name: 'By Officer' },
                      ] : activeTab === 'officer' ? [
                        { id: 'project', name: 'By Project' },
                        { id: 'groupJob', name: 'By Group Job' },
                        { id: 'user', name: 'By User QC' },
                      ] : []
                    }
                    onChange={(option) => setSubBreakdownType(option.id as any)}
                    buttonClassName="form-select form-select-sm border d-flex align-items-center justify-content-between text-start bg-white text-dark"
                    buttonStyle={{ width: '160px', borderRadius: '8px', padding: '6px 12px' }}
                  />
                </div>
                <input
                  type="text"
                  className="form-control form-control-sm border"
                  placeholder={`Search...`}
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={{ width: '200px', borderRadius: '8px', padding: '8px 12px' }}
                />
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead>
                  <tr style={{ background: '#f8fafc', color: '#475569', fontSize: '0.8rem' }}>
                    {activeTab === 'user' && (
                      <th style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>
                        QC User Name{renderSortIndicator('name')}
                      </th>
                    )}
                    {activeTab === 'project' && (
                      <th style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>
                        Project Name{renderSortIndicator('name')}
                      </th>
                    )}
                    {activeTab === 'groupJob' && (
                      <>
                        <th style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>
                          Job Name{renderSortIndicator('name')}
                        </th>
                        <th style={{ cursor: 'pointer' }} onClick={() => handleSort('projectName')}>
                          Project Name{renderSortIndicator('projectName')}
                        </th>
                      </>
                    )}
                    {activeTab === 'officer' && (
                      <th style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>
                        Field Officer Name{renderSortIndicator('name')}
                      </th>
                    )}

                    <th className="text-center" style={{ cursor: 'pointer' }} onClick={() => handleSort('approved')}>
                      Approved{renderSortIndicator('approved')}
                    </th>
                    <th className="text-center" style={{ cursor: 'pointer' }} onClick={() => handleSort('rejected')}>
                      Rejected{renderSortIndicator('rejected')}
                    </th>
                    <th className="text-center" style={{ cursor: 'pointer' }} onClick={() => handleSort('done')}>
                      Done{renderSortIndicator('done')}
                    </th>
                    <th className="text-center" style={{ cursor: 'pointer' }} onClick={() => handleSort('archived')}>
                      Archived{renderSortIndicator('archived')}
                    </th>
                    <th className="text-center" style={{ cursor: 'pointer' }} onClick={() => handleSort('total')}>
                      Total QC{renderSortIndicator('total')}
                    </th>
                    <th className="text-center" style={{ cursor: 'pointer' }} onClick={() => handleSort('avgDuration')}>
                      Avg Duration{renderSortIndicator('avgDuration')}
                    </th>
                  </tr>
                </thead>
                <tbody style={{ fontSize: '0.85rem' }}>
                  {paginatedList.length > 0 ? (
                    paginatedList.map((row) => (
                      <React.Fragment key={row.id}>
                        <tr>
                          {(activeTab === 'user' || activeTab === 'project' || activeTab === 'officer') && (
                            <td className="fw-semibold text-dark d-flex align-items-center gap-2">
                              <button
                                type="button"
                                className="btn btn-sm p-0 border-0 me-1"
                                onClick={() => toggleRowExpand(row.id)}
                                style={{ color: '#64748b', display: 'inline-flex', alignItems: 'center' }}
                              >
                                <i className={`fa-solid fa-chevron-${expandedRowIds[row.id] ? 'down' : 'right'}`} style={{ width: '12px' }}></i>
                              </button>
                              {activeTab === 'user' && (
                                row.profilePicture ? (
                                  <img
                                    src={row.profilePicture}
                                    alt={row.name}
                                    className="rounded-circle flex-shrink-0"
                                    style={{ width: '28px', height: '28px', objectFit: 'cover' }}
                                  />
                                ) : (
                                  <div
                                    className="rounded-circle d-flex align-items-center justify-content-center text-white font-bold flex-shrink-0"
                                    style={{ width: '28px', height: '28px', background: '#3b82f6', fontSize: '10px' }}
                                  >
                                    {row.name.split(' ').map((n: string) => n[0]).slice(0, 2).join('').toUpperCase()}
                                  </div>
                                )
                              )}
                              {activeTab === 'officer' && (
                                row.profilePicture ? (
                                  <img
                                    src={row.profilePicture}
                                    alt={row.name}
                                    className="rounded-circle flex-shrink-0"
                                    style={{ width: '28px', height: '28px', objectFit: 'cover' }}
                                  />
                                ) : (
                                  <div
                                    className="rounded-circle d-flex align-items-center justify-content-center text-white font-bold flex-shrink-0"
                                    style={{ width: '28px', height: '28px', background: '#64748b', fontSize: '10px' }}
                                  >
                                    {row.name.split(' ').map((n: string) => n[0]).slice(0, 2).join('').toUpperCase()}
                                  </div>
                                )
                              )}
                              {activeTab === 'officer' ? (
                                <div>
                                  <div className="fw-semibold text-dark">{row.name}</div>
                                  {row.tribeName && (
                                    <small className="text-muted" style={{ fontSize: '10px', display: 'block', fontWeight: 'normal' }}>
                                      Tribe: {row.tribeName}
                                    </small>
                                  )}
                                </div>
                              ) : (
                                <span>{row.name}</span>
                              )}
                            </td>
                          )}

                          {activeTab === 'groupJob' && (
                            <>
                              <td className="fw-semibold text-dark">
                                <button
                                  type="button"
                                  className="btn btn-sm p-0 border-0 me-2"
                                  onClick={() => toggleRowExpand(row.id)}
                                  style={{ color: '#64748b', display: 'inline-flex', alignItems: 'center' }}
                                >
                                  <i className={`fa-solid fa-chevron-${expandedRowIds[row.id] ? 'down' : 'right'}`} style={{ width: '12px' }}></i>
                                </button>
                                {row.name}
                              </td>
                              <td className="text-muted">{row.projectName}</td>
                            </>
                          )}

                          <td className="text-center text-success fw-semibold">{row.approved}</td>
                          <td className="text-center text-danger fw-semibold">{row.rejected}</td>
                          <td className="text-center text-warning fw-semibold">{row.done}</td>
                          <td className="text-center text-muted fw-semibold">{row.archived}</td>
                          <td className="text-center fw-bold">{row.total}</td>
                          <td className="text-center text-muted">{row.avgDuration}</td>
                        </tr>

                        {/* Expanded sub-breakdown flat rows (fully aligned with parent headers) */}
                        {expandedRowIds[row.id] && (() => {
                          let subRowsData: any[] = [];
                          if (subBreakdownType === 'project') subRowsData = row.projectsBreakdown || [];
                          else if (subBreakdownType === 'groupJob') subRowsData = row.jobsBreakdown || [];
                          else if (subBreakdownType === 'user') subRowsData = row.usersBreakdown || [];
                          else if (subBreakdownType === 'officer') subRowsData = row.officersBreakdown || [];

                          if (subRowsData.length === 0) {
                            return (
                              <tr style={{ backgroundColor: '#f8fafc' }}>
                                <td colSpan={activeTab === 'groupJob' ? 8 : 7} className="text-muted text-center py-2" style={{ fontSize: '0.8rem' }}>
                                  No breakdown data available.
                                </td>
                              </tr>
                            );
                          }

                          return subRowsData.map((subItem) => (
                            <tr key={subItem.id} style={{ backgroundColor: '#f8fafc', transition: 'background-color 0.2s' }}>
                              <td
                                className="text-muted fw-normal"
                                style={{ paddingLeft: '44px', fontSize: '0.825rem' }}
                                colSpan={activeTab === 'groupJob' ? 2 : 1}
                              >
                                <div className="d-flex align-items-center gap-2">
                                  <span className="text-primary-subtle fw-bold" style={{ fontSize: '0.85rem' }}>└─</span>
                                  {(subBreakdownType === 'user' || subBreakdownType === 'officer') && (
                                    subItem.profilePicture ? (
                                      <img
                                        src={subItem.profilePicture}
                                        alt={subItem.name}
                                        className="rounded-circle flex-shrink-0"
                                        style={{ width: '20px', height: '20px', objectFit: 'cover' }}
                                      />
                                    ) : (
                                      <div
                                        className="rounded-circle d-flex align-items-center justify-content-center text-white font-bold flex-shrink-0"
                                        style={{
                                          width: '20px',
                                          height: '20px',
                                          background: subBreakdownType === 'user' ? '#3b82f6' : '#64748b',
                                          fontSize: '8px'
                                        }}
                                      >
                                        {subItem.name.split(' ').map((n: string) => n[0]).slice(0, 2).join('').toUpperCase()}
                                      </div>
                                    )
                                  )}
                                  {subBreakdownType === 'officer' ? (
                                    <div className="text-start">
                                      <span className="fw-semibold text-dark d-block" style={{ fontSize: '0.825rem' }}>
                                        {subItem.name}
                                        {subItem.name === qcData.metrics.topRejectedOfficer?.name && (
                                          <span className="badge bg-danger-subtle text-danger ms-1" style={{ fontSize: '8px' }}>Top Rejects</span>
                                        )}
                                      </span>
                                      {subItem.tribeName && (
                                        <small className="text-muted d-block" style={{ fontSize: '9px', fontWeight: 'normal', lineHeight: 1.1 }}>
                                          Tribe: {subItem.tribeName}
                                        </small>
                                      )}
                                    </div>
                                  ) : (
                                    <span>{subItem.name}</span>
                                  )}
                                </div>
                              </td>
                              <td className="text-center text-success" style={{ fontSize: '0.825rem' }}>{subItem.approved}</td>
                              <td className="text-center text-danger" style={{ fontSize: '0.825rem' }}>{subItem.rejected}</td>
                              <td className="text-center text-warning" style={{ fontSize: '0.825rem' }}>{subItem.done}</td>
                              <td className="text-center text-muted" style={{ fontSize: '0.825rem' }}>{subItem.archived}</td>
                              <td className="text-center fw-semibold text-dark" style={{ fontSize: '0.825rem' }}>{subItem.total}</td>
                              <td className="text-center text-muted" style={{ fontSize: '0.825rem' }}>{subItem.avgDuration}</td>
                            </tr>
                          ));
                        })()}
                      </React.Fragment>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={activeTab === 'groupJob' ? 9 : 8} className="text-center py-4 text-muted">No QC records matched your filters.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center gap-3 mt-4 pt-3 border-top">
              <div className="me-4 d-flex align-items-center">
                <label className="me-2">Rows per page:</label>
                <CustomDropdown
                  value={pageSize}
                  options={PageSizeOption.options}
                  onChange={(opt) => {
                    setPageSize(Number(opt.id));
                    setCurrentPage(1);
                  }}
                  align="top"
                />
              </div>

              <div className="d-flex align-items-center gap-3">
                <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                  {`${(currentPage - 1) * pageSize + 1}–${Math.min(
                    currentPage * pageSize,
                    processedList.length
                  )} of ${processedList.length}`}
                </span>

                <nav>
                  <ul className="mb-0 pagination pagination-sm gap-1">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link border-0 rounded-circle" onClick={() => setCurrentPage(1)} style={{ width: '32px', height: '32px' }}>
                        <i className="fa fa-angle-double-left"></i>
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link border-0 rounded-circle" onClick={() => setCurrentPage(currentPage - 1)} style={{ width: '32px', height: '32px' }}>
                        <i className="fa fa-angle-left"></i>
                      </button>
                    </li>
                    <li className="page-item disabled">
                      <span className="page-link border-0 bg-transparent text-dark fw-bold px-2">{currentPage}</span>
                    </li>
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link border-0 rounded-circle" onClick={() => setCurrentPage(currentPage + 1)} style={{ width: '32px', height: '32px' }}>
                        <i className="fa fa-angle-right"></i>
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link border-0 rounded-circle" onClick={() => setCurrentPage(totalPages)} style={{ width: '32px', height: '32px' }}>
                        <i className="fa fa-angle-double-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm p-4 mt-4" style={{ borderRadius: '16px', background: '#ffffff' }}>
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 border-bottom pb-3 mb-3">
              <div>
                <h5 className="mb-1 fw-bold text-dark" style={{ letterSpacing: '-0.3px' }}>QC Daily Matrix</h5>
                <p className="text-muted mb-0" style={{ fontSize: '0.8rem' }}>
                  Day to day QC performance.
                </p>
                <span className="badge bg-primary-subtle text-primary mt-2" style={{ fontSize: '0.75rem', fontWeight: 600 }}>
                  Selected Project: {selectedProjectName}
                </span>
              </div>

              <div className="d-flex flex-wrap align-items-center gap-3">
                {/* Multiple Select Dropdown */}
                <div style={{ width: 220 }}>
                  <MultiSelectDropdown
                    options={dropdownOptions}
                    placeholder={selectedMatrixReviewers.length === 0 ? "All Reviewers" : "Select Reviewers..."}
                    value={selectedMatrixReviewers}
                    onChange={(selected) => setSelectedMatrixReviewers(selected)}
                    stayOpenOnUpdate={true}
                  />
                </div>

                {/* Daily vs Cumulative Toggle */}
                <div ref={matrixValueNavRef} className="nav-pills-animated">
                  <div
                    className="nav-pill-indicator"
                    style={matrixValueIndicatorStyle}
                  />
                  <button
                    type="button"
                    className={`nav-link btn ${matrixValueMode === 'daily' ? 'active' : ''}`}
                    onClick={() => setMatrixValueMode('daily')}
                  >
                    Daily
                  </button>
                  <button
                    type="button"
                    className={`nav-link btn ${matrixValueMode === 'cumulative' ? 'active' : ''}`}
                    onClick={() => setMatrixValueMode('cumulative')}
                  >
                    Cumulative
                  </button>
                </div>

                <div className="form-check form-switch mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="showMatrixAvgSwitch"
                    checked={showMatrixAvg}
                    onChange={(e) => setShowMatrixAvg(e.target.checked)}
                  />
                  <label className="form-check-label text-muted fw-semibold" htmlFor="showMatrixAvgSwitch" style={{ fontSize: '0.8rem', cursor: 'pointer' }}>
                    Show Average Duration
                  </label>
                </div>
                <div ref={matrixViewNavRef} className="nav-pills-animated">
                  <div
                    className="nav-pill-indicator"
                    style={matrixViewIndicatorStyle}
                  />
                  <button
                    type="button"
                    className={`nav-link btn ${matrixViewMode === 'table' ? 'active' : ''}`}
                    onClick={() => setMatrixViewMode('table')}
                    style={{ fontSize: '0.75rem', padding: '6px 12px' }}
                  >
                    <i className="fa-solid fa-table me-1"></i>Table
                  </button>
                  <button
                    type="button"
                    className={`nav-link btn ${matrixViewMode === 'chart' ? 'active' : ''}`}
                    onClick={() => setMatrixViewMode('chart')}
                    style={{ fontSize: '0.75rem', padding: '6px 12px' }}
                  >
                    <i className="fa-solid fa-chart-line me-1"></i>Chart
                  </button>
                </div>
              </div>
            </div>

            {qcData.dailyMatrix && qcData.dailyMatrix.length > 0 ? (
              matrixViewMode === 'table' ? (
                <div className="table-responsive animate__animated animate__fadeIn" style={{ overflowX: 'auto', position: 'relative' }}>
                  <table className="table table-bordered table-hover align-middle mb-0" style={{ borderCollapse: 'separate', borderSpacing: 0, minWidth: '1800px', borderColor: '#e2e8f0' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', color: '#475569', fontSize: '0.8rem' }}>
                        <th
                          className="fw-bold text-dark cursor-pointer"
                          onClick={() => handleMatrixSort('name')}
                          style={{ position: 'sticky', left: 0, backgroundColor: '#f8fafc', zIndex: 3, width: '220px', minWidth: '220px', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}
                        >
                          QC User Name {renderMatrixSortIndicator('name')}
                        </th>
                        <th
                          className="text-center fw-bold bg-light text-dark cursor-pointer"
                          onClick={() => handleMatrixSort('total')}
                          style={{ position: 'sticky', left: '220px', backgroundColor: '#f1f5f9', zIndex: 3, boxShadow: '2px 0 5px -2px rgba(0,0,0,0.15)', width: '100px', minWidth: '100px', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}
                        >
                          Total QC {renderMatrixSortIndicator('total')}
                        </th>
                        {qcData.dailyMatrix[0].days.map((d) => (
                          <th
                            key={d.date}
                            className="text-center fw-bold cursor-pointer"
                            onClick={() => handleMatrixSort(d.date)}
                            style={{ minWidth: '85px', whiteSpace: 'nowrap', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}
                          >
                            {d.date} {renderMatrixSortIndicator(d.date)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody style={{ fontSize: '0.85rem' }}>
                      {sortedMatrixRows.length === 0 ? (
                        <tr>
                          <td colSpan={2 + (qcData.dailyMatrix[0]?.days?.length ?? 0)} className="text-center py-4 text-muted">
                            No reviewers selected. Please select reviewers from the filter dropdown.
                          </td>
                        </tr>
                      ) : (
                        sortedMatrixRows.map((row: any) => {
                          const rowTotal = row.days.reduce((sum: number, d: any) => sum + d.count, 0);
                          return (
                            <tr key={row.userId}>
                              <td className="fw-semibold text-dark" style={{ position: 'sticky', left: 0, backgroundColor: '#ffffff', zIndex: 2, borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>
                                <div className="d-flex align-items-center gap-2">
                                  {row.profilePicture ? (
                                    <img
                                      src={row.profilePicture}
                                      alt={row.userName}
                                      className="rounded-circle flex-shrink-0"
                                      style={{ width: '24px', height: '24px', objectFit: 'cover' }}
                                    />
                                  ) : (
                                    <div
                                      className="rounded-circle d-flex align-items-center justify-content-center text-white font-bold flex-shrink-0"
                                      style={{ width: '24px', height: '24px', background: '#3b82f6', fontSize: '9px' }}
                                    >
                                      {row.userName.split(' ').map((n: string) => n[0]).slice(0, 2).join('').toUpperCase()}
                                    </div>
                                  )}
                                  {row.userName}
                                </div>
                              </td>
                              <td className="text-center fw-bold bg-light text-dark" style={{ position: 'sticky', left: '220px', backgroundColor: '#f8fafc', zIndex: 2, boxShadow: '2px 0 5px -2px rgba(0,0,0,0.15)', width: '100px', minWidth: '100px', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{rowTotal}</td>
                              {row.days.map((d: any, index: number) => {
                                const displayCount = matrixValueMode === 'cumulative'
                                  ? row.days.slice(0, index + 1).reduce((sum: number, day: any) => sum + day.count, 0)
                                  : d.count;
                                return (
                                  <td key={`${row.userId}-${d.date}-${index}`} className="text-center" style={{ minWidth: '85px', whiteSpace: 'nowrap', borderRight: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>
                                    <div className="fw-semibold text-dark">{displayCount}</div>
                                    {showMatrixAvg && d.count > 0 && (
                                      <div className="text-muted" style={{ fontSize: '0.7rem', marginTop: '2px' }}>
                                        <i className="fa-regular fa-clock me-1" style={{ fontSize: '0.65rem' }}></i>{d.avgDuration}
                                      </div>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })
                      )}
                    </tbody>

                  </table>
                </div>
              ) : (
                <div className="animate__animated animate__fadeIn p-2" style={{ height: '400px', position: 'relative' }}>
                  <Line data={matrixChartData} options={matrixChartOptions} plugins={[verticalHoverPlugin]} />
                </div>
              )) : (
              <div className="text-center py-4 text-muted">No daily matrix data available.</div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default QCMonitor;
